import React, { useState } from 'react';
import { Shield, Check, HelpCircle, ChevronDown, ChevronUp, Target, Clock, Award, FileCheck, Users, Zap, Package } from 'lucide-react';
import UniversalPopupForm from '../../components/UniversalPopupForm';
import SEO from '../../components/SEO';
import Footer from '../../components/Footer';

const AmazonBrandRegistry = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const benefits = [
    {
      title: 'Brand Protection',
      description: 'Protect against counterfeiters and unauthorized sellers with powerful Amazon brand protection tools.'
    },
    {
      title: 'Enhanced Content',
      description: 'Create richer product pages with A+ Content and enhanced brand storytelling capabilities.'
    },
    {
      title: 'Brand Analytics',
      description: 'Access powerful insights about customer search behavior and purchase patterns on Amazon.'
    },
    {
      title: 'Sponsored Brands',
      description: 'Unlock advanced advertising options including sponsored brand campaigns and video ads.'
    },
    {
      title: 'Stores Builder',
      description: 'Create your own multi-page Amazon Store to showcase your complete brand catalog.'
    },
    {
      title: 'Proactive Monitoring',
      description: 'Automated tools to identify and report potential trademark infringement on Amazon.'
    }
  ];

  const faqs = [
    {
      question: 'What are the requirements for Amazon Brand Registry?',
      answer: 'You need an active registered trademark from CIPO or another eligible trademark office, the ability to verify ownership, and products with your brand name or logo visible. We help you meet all requirements.'
    },
    {
      question: 'How long does it take to enroll in Amazon Brand Registry?',
      answer: 'Once you have your registered trademark, the Amazon Brand Registry enrollment typically takes 1-2 weeks. We guide you through the verification process to ensure smooth approval.'
    },
    {
      question: 'Do I need a trademark to join Amazon Brand Registry?',
      answer: 'Yes, an active registered trademark is required. We can help you obtain CIPO trademark registration, which is accepted by Amazon Brand Registry for Canadian sellers.'
    },
    {
      question: 'Can I protect my brand on Amazon without Brand Registry?',
      answer: 'Basic protections exist, but Brand Registry provides significantly more powerful tools for monitoring, reporting infringement, and controlling your brand presence on Amazon.'
    },
    {
      question: 'What if my trademark application is still pending?',
      answer: 'Amazon typically requires a registered trademark, not pending applications. We can help expedite your trademark registration to meet Brand Registry requirements as quickly as possible.'
    },
    {
      question: 'How much does Amazon Brand Registry cost?',
      answer: 'Amazon Brand Registry enrollment itself is free. However, you need a registered trademark first. Our services cover trademark registration and Brand Registry enrollment support.'
    }
  ];

  const amazonPricing = [
    {
      name: 'Basic',
      features: [
        'Trademark Application Filing',
        'CIPO Registration Process',
        'Basic Brand Registry Guidance',
        'Enrollment Instructions',
        'Email Support'
      ],
      recommended: false,
      badge: null
    },
    {
      name: 'Standard',
      features: [
        'Everything in Basic',
        'Comprehensive Trademark Search',
        'Priority Filing Service',
        'Amazon Registry Application',
        'Verification Support',
        'Brand Protection Setup Guide',
        'Phone & Email Support'
      ],
      recommended: true,
      badge: 'Recommended'
    },
    {
      name: 'Deluxe',
      features: [
        'Everything in Standard',
        'Multi-Class Trademark Filing',
        'Full Registry Enrollment Service',
        'A+ Content Setup Consultation',
        'Brand Analytics Training',
        'Sponsored Brands Setup',
        'Amazon Store Design Guidance',
        'Ongoing Brand Monitoring (6 months)',
        'Priority Support'
      ],
      recommended: false,
      badge: 'Best Seller'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Amazon Brand Registry Services Canada"
        description="Enroll in Amazon Brand Registry with expert help. Protect your brand on Amazon with advanced tools, enhanced content, and analytics."
        keywords="Amazon Brand Registry, Amazon seller protection, brand registry enrollment, Amazon trademark, protect Amazon listings"
      />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 overflow-hidden bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1598520106830-8c45c2035460"
            alt="Amazon marketplace and e-commerce"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-white bg-opacity-20 rounded-full px-4 py-2 mb-6">
              <span className="text-white font-semibold">Amazon Brand Protection</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">
              Amazon Brand Registry Services
            </h1>
            <p className="text-xl text-brand-100 leading-relaxed mb-8">
              Protect your brand on Amazon with trademark registration and Brand Registry enrollment. Complete support from experts.
            </p>
            <button
              onClick={() => setIsPopupOpen(true)}
              className="bg-white text-brand-600 font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200 shadow-xl"
            >
              Start Your Registration
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Professional Help */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1563986768609-322da13575f3"
                alt="Amazon brand protection and trademark services"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-green-100 rounded-full p-2">
                    <Award className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="font-bold text-gray-900">Amazon Experts</span>
                </div>
                <p className="text-sm text-gray-600">Brand Registry specialists</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Advantage
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Professional Help Matters
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Amazon Brand Registry requires a registered trademark and proper setup. Our experts handle both trademark registration and Brand Registry enrollment, ensuring you unlock all protection features.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Target className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Complete Trademark Service</h3>
                    <p className="text-gray-600">We handle your CIPO trademark registration, the prerequisite for Brand Registry.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Brand Registry Enrollment</h3>
                    <p className="text-gray-600">Expert guidance through Amazon's verification and enrollment process.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Save Time & Money</h3>
                    <p className="text-gray-600">Avoid mistakes in trademark filing or Brand Registry enrollment that could delay protection.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Benefits of Amazon Brand Registry
            </h2>
            <p className="text-lg text-gray-600">
              Why every Amazon seller needs Brand Registry protection
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-brand-50 to-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-brand-100 min-w-full md:min-w-0 snap-center"
              >
                <div className="bg-brand-600 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amazon Brand Registry Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-green-100 text-green-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Amazon Expertise
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Navigate Amazon Requirements With Confidence
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Amazon Brand Registry has specific requirements and verification processes. Our team has successfully enrolled hundreds of brands and knows exactly how to navigate Amazon's system efficiently.
              </p>
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl">What We Handle:</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">CIPO trademark registration process</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Amazon Brand Registry application</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Verification and enrollment support</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Brand protection tool setup</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Ongoing brand monitoring guidance</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Start Your Application
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-500 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1531973576160-7125cd663d86"
                alt="Amazon Brand Registry process and enrollment"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Amazon Registry</div>
                    <div className="text-sm text-gray-600">Experts</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fast & Reliable Service */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c"
                alt="Fast Amazon Brand Registry processing"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-brand-600 to-brand-800 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <Zap className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">Full Service</div>
                <div className="text-sm text-brand-100">Trademark to Registry</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Quick & Efficient
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Fast Processing, Quality Results
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We understand that protecting your Amazon brand is critical to your business success. Our streamlined process gets you enrolled in Brand Registry as quickly as possible.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Efficient Process</h3>
                    <p className="text-gray-600">From trademark filing to Brand Registry enrollment, we handle everything efficiently.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Dedicated Support</h3>
                    <p className="text-gray-600">Personal case manager guides you through trademark and Amazon enrollment steps.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-800 rounded-full p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Proven Success</h3>
                    <p className="text-gray-600">Hundreds of successful Brand Registry enrollments for Canadian sellers.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our 3-Step Registry Process
            </h2>
            <p className="text-lg text-gray-600">
              Simple, transparent, and complete Amazon Brand Registry
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 pt-8">
            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-full md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                1
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Trademark Registration
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  We conduct a comprehensive search and file your trademark application with CIPO, the prerequisite for Amazon Brand Registry enrollment.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-full md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                2
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Brand Registry Application
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Once your trademark is registered, we guide you through the Amazon Brand Registry application and verification process.
                </p>
              </div>
            </div>

            <div className="relative bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg border border-brand-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl min-w-full md:min-w-0 snap-center">
              <div className="absolute -top-6 left-8 bg-gradient-to-r from-brand-600 to-brand-800 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                3
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Protection & Tools
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Access all Brand Registry features including A+ Content, Brand Analytics, and automated brand protection tools to safeguard your Amazon presence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Amazon Brand Registry Packages
            </h2>
            <p className="text-lg text-gray-600">
              Complete trademark and Brand Registry solutions
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {amazonPricing.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 ${
                  plan.recommended ? 'ring-2 ring-brand-600 scale-105' : ''
                }`}
              >
                {plan.badge && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-brand-600 to-brand-800 text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    {plan.badge}
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
                  <div className="mb-6">
                    <p className="text-gray-600 text-sm">Contact us for pricing</p>
                  </div>
                  <ul className="space-y-3 mb-8 max-h-64 overflow-y-auto">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setIsPopupOpen(true)}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      plan.recommended
                        ? 'bg-gradient-to-r from-brand-600 to-brand-800 text-white hover:from-brand-700 hover:to-brand-900'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Get a Quote
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Common questions about Amazon Brand Registry
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="relative lg:sticky lg:top-8">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0"
                alt="Amazon Brand Registry consultation"
                className="relative rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2 text-lg">Still Have Questions?</h3>
                <p className="text-gray-600 text-sm mb-4">Our Amazon Brand Registry experts are here to help answer all your questions.</p>
                <button
                  onClick={() => { if (window.zE) { window.openZendeskChat && window.openZendeskChat(); } else { alert('Chat is loading... Please try again in a moment.'); } }}
                  className="w-full bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold py-2 px-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transition-all text-sm"
                >
                  Contact Us
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4 flex-1">
                      <HelpCircle className="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" />
                      <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                    </div>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <div className="pl-10 text-gray-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
            alt="Amazon marketplace and brand protection"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/95 via-brand-800/90 to-brand-700/95"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-4">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-4 py-2 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Trusted by Amazon Sellers</span>
            </div>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            Stop Counterfeiters & Hijackers<br />
            <span className="bg-gradient-to-r from-brand-300 to-brand-400 bg-clip-text text-transparent">
              Protect Your Amazon Business
            </span>
          </h2>
          
          <p className="text-lg text-brand-100 mb-8 max-w-3xl mx-auto">
            Get trademark registration and Amazon Brand Registry enrollment from Canada's trusted experts.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="group relative bg-white text-brand-600 font-bold px-8 py-3 rounded-xl hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span>Protect Your Amazon Brand</span>
              </span>
            </button>
            <button
              onClick={() => { if (window.zE) { window.openZendeskChat && window.openZendeskChat(); } else { alert('Chat is loading... Please try again in a moment.'); } }}
              className="bg-gradient-to-r from-brand-600 to-brand-800 text-white font-bold px-8 py-3 rounded-xl hover:from-brand-700 hover:to-brand-900 transform hover:scale-105 transition-all duration-300 shadow-2xl"
            >
              Chat Now
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold text-white mb-1">100%</div>
              <div className="text-brand-100 text-xs">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold text-white mb-1">Fast</div>
              <div className="text-brand-100 text-xs">Enrollment</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold text-white mb-1">500+</div>
              <div className="text-brand-100 text-xs">Brands Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
              <div className="text-3xl font-bold text-white mb-1">Expert</div>
              <div className="text-brand-100 text-xs">Support</div>
            </div>
          </div>

          <p className="text-brand-200 mt-6 text-xs">
            Protecting Amazon sellers coast to coast • Brand Registry Specialists
          </p>
        </div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
      
      <Footer />
    </div>
  );
};

export default AmazonBrandRegistry;
